import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@rapid/contexts/AuthContext";
import * as workspaceApi from "@rapid/utils/api/workspace";

export const workspaceKeys = {
    all: ["workspaces"] as const,
    myWorkspaces: (userId: string) => [...workspaceKeys.all, "my", userId] as const,
    apiKey: (workspaceId: string) => [...workspaceKeys.all, "apiKey", workspaceId] as const
};

export function useWorkspaces() {
    const { user, activeSession } = useAuth();

    return useQuery({
        queryKey: workspaceKeys.myWorkspaces(user?.uid ?? ""),
        queryFn: async () => {
            const workspaceCreationName = user?.displayName
                ? `${user.displayName}'s Workspace`
                : undefined;

            const result = await workspaceApi.loadMyWorkspaces({ workspaceCreationName });

            if (result.created && result.workspaces.length > 0) {
                const updatedUsage = await workspaceApi.setupRapidCreatedWorkspace({
                    workspaceId: result.workspaces[0].id
                });
                return { workspaces: result.workspaces, usages: [updatedUsage] };
            }

            return { workspaces: result.workspaces, usages: result.usages };
        },
        enabled: !!user && activeSession,
        staleTime: Infinity
    });
}

export function useApiKey(workspaceId: string | null | undefined) {
    return useQuery({
        queryKey: workspaceKeys.apiKey(workspaceId ?? ""),
        queryFn: async () => {
            const response = await workspaceApi.getApiKey(workspaceId!);
            return (response?.apiKey as string) ?? null;
        },
        enabled: !!workspaceId,
        staleTime: Infinity,
        gcTime: Infinity
    });
}

export function useClearWorkspaceQueries() {
    const queryClient = useQueryClient();
    return useCallback(
        () => queryClient.removeQueries({ queryKey: workspaceKeys.all }),
        [queryClient]
    );
}
